import { EventInput } from '@fullcalendar/core'
import { IconifyIcons } from '@thyme/nashville/src/types/icons'
import get from 'lodash-es/get'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { nashColors } from 'tailwind.config.ts'
// nosemgrep: no-v2-imports
import { SCHEDULED_CALL_TIME_SET_DURATION } from '@/legacy/components/patient/communicationV2/lib/plannedCalls'
// nosemgrep: no-legacy-api-store
import apiStore from '@/legacy/store/modules/apiBuilder'
import { Disposition } from '@/legacy/types/communications/callDispositions'
import {
  Communication,
  CommunicationType,
  CommunicationParts,
} from '@/legacy/types/communications/communications'
import { GenericListResponse } from '@/libs/api/types'
import { getPlannedCallDisplayDateTime } from '@/libs/date/plannedCallDateFormatting'
import { formatNameFromEntity } from '@/libs/format/format'
import { DEFAULT_END_TIME_OFFSET_MINUTES } from '@/pages/PatientProfile/CommunicationSidebar/shared/useScheduledCallEndTime'
import { useFlagStore } from '@/store/flags/flags'

export const useScheduleApi = apiStore<EventInput, EventInput[], Communication>(
  'scheduleApi',
  '/api/communications',
  {
    transformData: (d: GenericListResponse<Communication>) => {
      const { showScheduledCallsDueDate, showScheduledStartAndEndTime } =
        storeToRefs(useFlagStore())

      return {
        ...d,
        data: d.data.map((communication: Communication) => {
          // If call was completed, use its actual time of completion in My Schedule
          // If call is still scheduled, use the scheduled datetime
          let startTime: string | null | undefined

          if (communication.completedDatetime) {
            startTime = communication.completedDatetime
          } else if (communication.plannedCall) {
            // Backend always sends dueDate for planned calls
            startTime = getPlannedCallDisplayDateTime(
              communication.plannedCall,
              showScheduledCallsDueDate.value
            )
          }

          // convert to UTC (only add Z if not already present). Skip date-only
          // values (no due_datetime set) - appending Z to those produces an
          // invalid ISO string that FullCalendar silently fails to parse.
          if (
            startTime &&
            startTime.includes('T') &&
            !startTime.endsWith('Z')
          ) {
            startTime += 'Z'
          }
          const endDatetime = communication.plannedCall?.endDatetime
          let endTime: string | null

          if (showScheduledStartAndEndTime.value && endDatetime) {
            // endDatetime is the source of truth for the real scheduled end
            // time when this flag is on
            endTime = endDatetime.endsWith('Z')
              ? endDatetime
              : `${endDatetime}Z`
          } else if (
            communication.plannedCall?.duration == null &&
            showScheduledStartAndEndTime.value
          ) {
            // no duration sentinel from the backend to fall back on - default
            // to the flag's end-time offset instead of the legacy 30-minute one
            endTime = startTime
              ? DateTime.fromISO(startTime)
                  .plus({ minutes: DEFAULT_END_TIME_OFFSET_MINUTES })
                  .toISO()
              : null
          } else {
            const duration =
              communication.plannedCall?.duration ??
              SCHEDULED_CALL_TIME_SET_DURATION

            // if start time specified but duration is not 30 minutes, the call is scheduled
            // but only for a day and not for a specific time, so do not set end time
            endTime =
              startTime && duration === SCHEDULED_CALL_TIME_SET_DURATION
                ? DateTime.fromISO(startTime)
                    .plus({ minutes: duration })
                    .toISO()
                : null
          }
          const patient = get(communication, 'patients[0]')
          const callWith = patient ? formatNameFromEntity(patient) : 'UNKNOWN'
          let eventColor
          let textColor
          let icon
          if (
            communication.callDisposition?.disposition === Disposition.ANSWERED
          ) {
            eventColor = nashColors.fern100
            textColor = nashColors.fern700
            icon = IconifyIcons.check
          } else if (communication.callDisposition) {
            eventColor = nashColors.neutral200
            textColor = nashColors.neutral700
            icon = IconifyIcons.close
          }

          // put in "no call time" bucket (all day) if we do not want to display time
          let allDay = false
          // explicitly check for showTime === false, we do want to display in other scenarios
          if (
            !communication.callDisposition &&
            communication.plannedCall?.showTime === false
          ) {
            allDay = true
          }

          return {
            title: `Call with ${callWith}`,
            allDay,
            start: startTime,
            end: endTime,
            id: communication.communicationId,
            ...(eventColor
              ? { backgroundColor: eventColor, borderColor: eventColor }
              : {}),
            ...(textColor ? { textColor } : {}),
            extendedProps: {
              ...(icon ? { icon } : {}),
              patientId: patient?.entityId,
            },
          } as EventInput
        }),
      }
    },
    params: {
      parts: [
        CommunicationParts.planned,
        CommunicationParts.disposition,
        CommunicationParts.patients,
      ],
      filter_types: [CommunicationType.Call],
      filter_unassigned: 'EXCLUDE',
    },
  }
)
